import { useCallback, useEffect, useRef, useState } from 'react';

type CallbackFunction = () => boolean | string;

function useIntervalConditionally(callback: CallbackFunction, delay: number | null, maxIterations?: number | null, shouldRun: boolean = true) {
  const savedCallback = useRef<CallbackFunction>(() => false); // Initialize with a function that returns a boolean
  const iterationCount = useRef(0);
  const [finishedObject, setFinished] = useState({ finished: false, callbackValue: '' });
  const isRunningRef = useRef(false);

  const maxIterationsReached = useCallback(() => {
    if (maxIterations && iterationCount.current >= maxIterations) {
      return true;
    }

    return false;
  }, [maxIterations]);

  /* Remember the callback. */
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  /*
   - Set up the interval.
   - Communicate with caller to determine if predicate fn passed every tick
      i. if so, clear interval
   - If number ticks exceed max, clear interval.
   */
  useEffect(() => {
    function tick(id: ReturnType<typeof setInterval>) {
      const done = savedCallback.current();

      if (done || maxIterationsReached()) {
        setFinished({ finished: true, callbackValue: typeof done === 'boolean' ? done.toString() : done });
        clearInterval(id);
        isRunningRef.current = false;
      }

      iterationCount.current++;
    }

    if (delay !== null && shouldRun && !isRunningRef.current) {
      // Check if the interval is not already running
      isRunningRef.current = true;
      const id: ReturnType<typeof setInterval> = setInterval(() => tick(id), delay);
      return () => {
        clearInterval(id);
        isRunningRef.current = false;
      };
    }
    return () => {};
  }, [delay, shouldRun, maxIterationsReached]);

  return { iterationCount, ...finishedObject };
}

export default useIntervalConditionally;
